import { type FC } from "react";

import { ActorLevelChip } from "../ActorLevelChip";
import { type BlackboardInstance } from "../blackboardModels";
import { ModelChip } from "../ModelChip";
import { ContextWindowDisplay } from "./ContextWindowDisplay";
import { CooldownDisplay } from "./CooldownDisplay";
import { COMPACT_GROWTH_THRESHOLD_FALLBACK } from "./helpers";
import { InfoRow } from "./InfoRow";
import { TokenStackBar } from "./TokenStackBar";

// ---------------------------------------------------------------------------
// RuntimeSection — the live-at-a-glance runtime facts: model, level, current
// run, token usage, run count + restarts, cooldown, evolution, context window.
// ---------------------------------------------------------------------------

export const RuntimeSection: FC<{ instance: BlackboardInstance }> = ({
  instance,
}) => (
  <>
    {instance.modelRef && (
      <InfoRow label="Model">
        <ModelChip modelRef={instance.modelRef} />
      </InfoRow>
    )}
    <InfoRow label="Level">
      <ActorLevelChip level={instance.evolution} />
    </InfoRow>
    <InfoRow label="Instance ID">
      <code className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
        {instance.id}
      </code>
    </InfoRow>
    {instance.currentRunId && (
      <InfoRow label="Current Run">
        <code className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
          {instance.currentRunId}
        </code>
      </InfoRow>
    )}
    <InfoRow label="Tokens">
      <TokenStackBar tokenUsage={instance.tokenUsage} />
    </InfoRow>
    <InfoRow label="Run Count">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] tabular-nums text-slate-700 dark:text-slate-200">
          {instance.runCount}
        </span>
        {instance.runCount > 1 && (
          <span className="font-mono text-[10px] text-amber-500 dark:text-amber-400">
            {instance.runCount - 1} restart
            {instance.runCount - 1 !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    </InfoRow>
    {instance.tickIntervalMs !== null && (
      <InfoRow label="Cooldown">
        <CooldownDisplay
          tickIntervalMs={instance.tickIntervalMs}
          lastParkedAt={instance.lastParkedAt}
          status={instance.status}
        />
      </InfoRow>
    )}
    <InfoRow label="Evolution">
      {instance.evolution === 0 ? (
        <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
          Gen 0
        </span>
      ) : (
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-[11px] font-bold tabular-nums text-violet-600 dark:text-violet-400">
            Gen {instance.evolution}
          </span>
          <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
            compact cycles
          </span>
        </div>
      )}
    </InfoRow>
    {instance.contextWindow !== null && (
      <InfoRow label="Context">
        <ContextWindowDisplay
          contextWindow={instance.contextWindow}
          compactThreshold={
            instance.overrides?.compactContextGrowthThreshold ??
            COMPACT_GROWTH_THRESHOLD_FALLBACK
          }
        />
      </InfoRow>
    )}
  </>
);
